import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Rocket, Zap, Crown, Sparkles, Trophy, Store, Coins, Snowflake, DollarSign, Loader2, Plus, ArrowUpRight, Flame } from 'lucide-react';

const ICONS = { rocket: Rocket, zap: Zap, crown: Crown, sparkles: Sparkles, trophy: Trophy, snowflake: Snowflake };

function CoinBuyDialog({ open, onClose, packages, onCheckout, loadingId }) {
  if (!open) return null;
  return (
    <div
      data-testid="coin-buy-dialog"
      className="fixed inset-0 z-[90] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-3"
      onClick={onClose}
    >
      <div className="w-full max-w-md glass rounded-3xl p-5 border border-amber-500/30 shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-4">
          <div className="w-9 h-9 rounded-xl gradient-gold flex items-center justify-center">
            <Coins className="w-5 h-5 text-slate-950" />
          </div>
          <div>
            <div className="font-display font-black text-xl">Buy Coins</div>
            <div className="text-xs text-slate-400">Secure checkout via Stripe. Coins never expire.</div>
          </div>
        </div>
        <div className="space-y-2">
          {packages.map(p => (
            <button
              key={p.id}
              data-testid={`coin-pack-${p.id}`}
              onClick={() => onCheckout(p)}
              disabled={loadingId === p.id}
              className={`w-full flex items-center justify-between rounded-xl border p-3 text-left transition-colors
                ${p.popular ? 'border-amber-400/60 bg-amber-500/10' : 'border-white/10 bg-white/[0.04] hover:bg-white/[0.08]'}
                ${loadingId === p.id ? 'opacity-60' : ''}`}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg gradient-gold flex items-center justify-center">
                  <Coins className="w-5 h-5 text-slate-950" />
                </div>
                <div>
                  <div className="font-display font-black text-lg">{p.title}</div>
                  {p.popular && <div className="text-[10px] uppercase tracking-wider text-amber-300 font-bold">Best value</div>}
                </div>
              </div>
              <div className="text-right">
                <div className="font-display font-black text-lg">${p.price_usd.toFixed(2)}</div>
                {loadingId === p.id ? <Loader2 className="w-4 h-4 animate-spin ml-auto text-amber-300" /> : null}
              </div>
            </button>
          ))}
        </div>
        <div className="text-[10px] text-slate-500 mt-4 text-center">
          Coins are a paid currency and are separate from JXP (your activity points).
        </div>
      </div>
    </div>
  );
}

export default function CoinShop() {
  const { user, refresh } = useAuth();
  const [items, setItems] = useState([]);
  const [packages, setPackages] = useState([]);
  const [buying, setBuying] = useState(null);
  const [buyOpen, setBuyOpen] = useState(false);
  const [checkoutId, setCheckoutId] = useState(null);
  const [txns, setTxns] = useState([]);

  const loadAll = async () => {
    const [i, p, w] = await Promise.all([
      api.get('/coinshop/items'),
      api.get('/coins/packages'),
      api.get('/coins/wallet').catch(() => ({ data: { coins_balance: 0, transactions: [] } })),
    ]);
    setItems(i.data);
    setPackages(p.data);
    setTxns((w.data?.transactions || []).slice(0, 10));
  };
  useEffect(() => { loadAll(); }, []);

  const buy = async (sku) => {
    setBuying(sku);
    try {
      await api.post('/coinshop/purchase', { sku });
      toast.success('Purchased! 🎉');
      refresh();
      loadAll();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Failed';
      toast.error(detail);
      if (e?.response?.status === 402) setBuyOpen(true);
    }
    finally { setBuying(null); }
  };

  const startCheckout = async (pkg) => {
    setCheckoutId(pkg.id);
    try {
      const origin = window.location.origin;
      const { data } = await api.post('/coins/checkout', { package_id: pkg.id, origin_url: origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Checkout failed');
    } finally {
      setCheckoutId(null);
    }
  };

  const coinBal = user?.coins_balance || 0;

  return (
    <div className="max-w-5xl mx-auto space-y-5 pb-24">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold flex items-center gap-2">
            <Store className="w-7 h-7 text-amber-400" /> Coin Shop
          </h1>
          <p className="text-sm text-slate-400">Spend Coins on streak freezes, boosts, premium trials & perks.</p>
        </div>
        {user && (
          <div className="flex items-center gap-2">
            <div data-testid="shop-coins-balance" className="flex items-center gap-1.5 px-3 py-1.5 rounded-full gradient-gold text-slate-950 font-bold text-sm shadow-lg shadow-amber-500/20">
              <Coins className="w-4 h-4" /> {coinBal.toLocaleString()} Coins
            </div>
            <Button data-testid="buy-coins-btn" size="sm" onClick={() => setBuyOpen(true)}
                    className="gradient-brand text-white font-bold">
              <Plus className="w-3.5 h-3.5 mr-1" /> Buy Coins
            </Button>
          </div>
        )}
      </div>

      {/* JXP vs Coins helper banner */}
      <Card className="glass p-3 border border-white/10 flex flex-wrap items-center gap-4 text-xs">
        <div className="flex items-center gap-1.5">
          <Flame className="w-3.5 h-3.5 text-orange-400" />
          <span className="text-slate-400">You have</span>
          <span className="font-bold text-orange-300">{(user?.jxp || 0).toLocaleString()} JXP</span>
          <span className="text-slate-500">(engagement points — not money)</span>
        </div>
        <div className="h-3 w-px bg-white/10" />
        <div className="flex items-center gap-1.5">
          <Coins className="w-3.5 h-3.5 text-amber-400" />
          <span className="text-slate-400">And</span>
          <span className="font-bold text-amber-300">{coinBal.toLocaleString()} Coins</span>
          <span className="text-slate-500">(paid currency used here)</span>
        </div>
      </Card>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map(it => {
          const Icon = ICONS[it.icon] || Sparkles;
          const canAfford = coinBal >= it.price_coins;
          return (
            <Card key={it.sku} data-testid={`shop-item-${it.sku}`} className="glass p-5">
              <div className="w-11 h-11 rounded-xl gradient-brand flex items-center justify-center mb-3">
                <Icon className="w-5 h-5 text-white" />
              </div>
              <div className="font-display font-bold">{it.title}</div>
              <div className="text-xs text-slate-400 mt-1 leading-relaxed">{it.desc}</div>
              <div className="flex items-center justify-between mt-4">
                <div className="font-display font-black text-amber-400 flex items-center gap-1">
                  <Coins className="w-4 h-4" /> {it.price_coins.toLocaleString()}
                </div>
                <Button
                  data-testid={`shop-buy-${it.sku}`}
                  size="sm"
                  disabled={buying === it.sku}
                  onClick={() => canAfford ? buy(it.sku) : setBuyOpen(true)}
                  className={canAfford ? 'gradient-brand text-white' : 'bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold'}
                >
                  {buying === it.sku ? '…' : (canAfford ? 'Buy' : 'Get Coins')}
                </Button>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Transaction history */}
      {user && (
        <Card className="glass p-4" data-testid="coin-txns-card">
          <div className="flex items-center justify-between mb-3">
            <div className="font-display font-bold flex items-center gap-2">
              <ArrowUpRight className="w-4 h-4 text-amber-300" /> Recent Coin activity
            </div>
            <Link to="/coinshop" className="text-xs text-violet-300 hover:text-violet-200">All transactions →</Link>
          </div>
          {txns.length === 0 ? (
            <div className="text-xs text-slate-500 text-center py-6">No transactions yet. Buy Coins to get started.</div>
          ) : (
            <div className="space-y-1.5">
              {txns.map(t => (
                <div key={t.id} className="flex items-center justify-between text-xs py-1.5 border-b border-white/5 last:border-0">
                  <div>
                    <div className="font-semibold capitalize">{(t.reason || '').replace(/_/g, ' ')}</div>
                    <div className="text-[10px] text-slate-500">{new Date(t.created_at).toLocaleString()}</div>
                  </div>
                  <div className={`font-bold ${t.amount >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>
                    {t.amount >= 0 ? '+' : ''}{t.amount} <span className="text-[10px]">Coins</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      <CoinBuyDialog
        open={buyOpen}
        onClose={() => setBuyOpen(false)}
        packages={packages}
        onCheckout={startCheckout}
        loadingId={checkoutId}
      />
    </div>
  );
}
